import { useEffect, useState } from 'react';
import { deriveListPage } from '../lib/list';

export type ContactSortBy = 'label' | 'displayName' | 'companyName' | 'createdAt' | 'updatedAt';
export type ContactSortDir = 'asc' | 'desc';

export interface ContactsFilters {
  typeId?: number;
  ownerId?: number;
  companyName?: string;
  hasEmail?: boolean;
  hasPhone?: boolean;
  sortBy: ContactSortBy;
  sortDir: ContactSortDir;
}

export const DEFAULT_FILTERS: ContactsFilters = {
  sortBy: 'createdAt',
  sortDir: 'desc',
};

export const CONTACTS_PAGE_SIZE = 25;

export const SORT_OPTIONS: { value: `${ContactSortBy}:${ContactSortDir}`; label: string }[] = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'label:asc', label: 'Name (A–Z)' },
  { value: 'label:desc', label: 'Name (Z–A)' },
  { value: 'companyName:asc', label: 'Company (A–Z)' },
  { value: 'updatedAt:desc', label: 'Recently updated' },
];

export function hasActiveContactsFilters(searchInput: string, filters: ContactsFilters): boolean {
  return (
    !!searchInput ||
    !!filters.typeId ||
    !!filters.ownerId ||
    !!filters.companyName ||
    filters.hasEmail !== undefined ||
    filters.hasPhone !== undefined
  );
}

/**
 * Owns debounced free-text search input, filter state, and offset pagination
 * cursor for the Contacts list — no tRPC dependency, so it can be wrapped by
 * any tRPC client shape (portal `createTRPCReact` hooks, extension
 * `@trpc/tanstack-react-query` proxy, etc.) without duplicating the debounce
 * or cursor-reset logic per app. `initial` seeds first-mount state (e.g. from
 * a URL — see `useContactsSearch`'s portal-only URL sync) and is otherwise
 * ignored on re-renders, same as any `useState` initial value.
 */
export function useContactsSearchState(initial?: {
  searchInput?: string;
  filters?: ContactsFilters;
  cursor?: number;
}) {
  const [searchInput, setSearchInput] = useState(initial?.searchInput ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState((initial?.searchInput ?? '').trim());
  const [filters, setFiltersState] = useState<ContactsFilters>(initial?.filters ?? DEFAULT_FILTERS);
  const [cursor, setCursor] = useState(initial?.cursor ?? 0);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Any change to the effective query/filters invalidates the current page. Reset
  // during render (React's documented pattern for "adjusting state when inputs
  // change") rather than in an effect, which would cause an extra cascading render.
  const resetKey = JSON.stringify({ debouncedQuery, filters });
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setCursor(0);
  }

  const setFilter = <K extends keyof ContactsFilters>(key: K, value: ContactsFilters[K]) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFiltersState(DEFAULT_FILTERS);
    setSearchInput('');
  };

  return {
    searchInput,
    setSearchInput,
    debouncedQuery,
    filters,
    setFilter,
    resetFilters,
    cursor,
    setCursor,
  };
}

export type ContactsSearchState = ReturnType<typeof useContactsSearchState>;

/** Shapes the `contacts.search` tRPC input from search state — no tRPC import. */
export function buildContactsSearchInput(state: ContactsSearchState) {
  return {
    query: state.debouncedQuery || undefined,
    typeId: state.filters.typeId,
    ownerId: state.filters.ownerId,
    companyName: state.filters.companyName || undefined,
    hasEmail: state.filters.hasEmail,
    hasPhone: state.filters.hasPhone,
    sortBy: state.filters.sortBy,
    sortDir: state.filters.sortDir,
    cursor: state.cursor,
    limit: CONTACTS_PAGE_SIZE,
  };
}

/** @deprecated Use `deriveListPage` from `lib/list` — kept as an alias for existing call sites. */
export const deriveContactsPage = deriveListPage;
